'use no memo'
import { CSSProperties, useRef } from 'react'

import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { HorizontalRulePlugin } from '@lexical/react/LexicalHorizontalRulePlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { TablePlugin } from '@lexical/react/LexicalTablePlugin'

import { RichEditorContextProvider } from './components/ContextProvider'
import { useLexicalConfig } from './hooks/useLexicalConfig'
import { useStoreConfig } from './hooks/useStoreConfig'
import { useTraceEditorContentSize } from './hooks/useTraceEditorContentSize'
import { RichEditorProps } from './interface'
import { AttachmentPlugin } from './plugins/AttachmentPlugin'
import { DraggableBlockPlugin } from './plugins/DraggableBlockPlugin'
import { EmojiPlugin } from './plugins/EmojiPlugin'
import { FormatPainterPlugin } from './plugins/FormatPainterPlugin'
import { ImagePlugin } from './plugins/ImagePlugin'
import { LinkPlugin } from './plugins/LinkPlugin'
import { ModePlugin } from './plugins/ModePlugin'
import { QuotePlugin } from './plugins/QuotePlugin'
import { ShortcutsPlugin } from './plugins/ShortcutsPlugin'
import { StatePlugin } from './plugins/StatePlugin'
import { TabIndentationPlugin } from './plugins/TabIndentationPlugin'
import { TableCellResizerPlugin } from './plugins/TableCellResizerPlugin'
import { TableContextMenuPlugin } from './plugins/TableContextMenuPlugin'
import { TableControllerPlugin } from './plugins/TableControllerPlugin'
import { TableOfContentsPlugin } from './plugins/TableOfContentsPlugin'
import { TableWrapperPlugin } from './plugins/TableWrapperPlugin'
import { TreeViewPlugin } from './plugins/TreeViewPlugin'
import { useEditorStyles } from './style'
import { EDITOR_TOOLBAR_HEIGHT } from './style/constants'
import { Toolbar } from './Toolbar'
import { PreviewAttachment } from './ui/PreviewAttachment'

export const RichEditor = ({
  className,
  style,
  debug = false,
  readPretty = false,
  toolbar = true,
  tableOfContents = false,
  disabled = false,
  bordered = true,
  value,
  onChange
}: RichEditorProps) => {
  const lexicalConfig = useLexicalConfig()
  const storeConfig = useStoreConfig()

  const floatingAnchorRef = useRef<HTMLDivElement>(null)
  const editorContentRef = useTraceEditorContentSize()

  const tableOfContentsProps =
    typeof tableOfContents === 'object' ? tableOfContents : {}

  const mergedToolbar = readPretty ? false : toolbar
  const mergedTableOfContents = readPretty ? true : tableOfContents
  const mergedDisabled = readPretty ? true : disabled
  const mergedBordered = readPretty ? false : bordered

  const { cx, styles } = useEditorStyles()

  const editorStyle: CSSProperties = {
    fontFamily: storeConfig.defaultFontFamily,
    fontSize: storeConfig.defaultFontSize,
    color: storeConfig.defaultFontColor,
    lineHeight: storeConfig.defaultLineHeight,
    ...style
  }

  return (
    <RichEditorContextProvider
      lexicalConfig={lexicalConfig}
      storeConfig={storeConfig}
    >
      <div
        style={editorStyle}
        className={cx(styles.editor, 'rich-editor', className, {
          ['is-bordered']: mergedBordered
        })}
      >
        {mergedToolbar && (
          <Toolbar className={cx(styles.toolbar, 'rich-editor-toolbar')} />
        )}
        <div className={cx(styles.body, 'rich-editor-body')}>
          <div
            ref={floatingAnchorRef}
            className={cx(styles.main, 'rich-editor-main')}
          >
            <RichTextPlugin
              ErrorBoundary={LexicalErrorBoundary}
              contentEditable={
                <ContentEditable
                  ref={editorContentRef}
                  className={cx(styles.content, 'rich-editor-content', {
                    'is-preview': readPretty
                  })}
                />
              }
              placeholder={
                readPretty ? null : (
                  <div
                    className={cx(
                      styles.placeholder,
                      'rich-editor-placeholder'
                    )}
                  >
                    请输入内容...
                  </div>
                )
              }
            />
          </div>
          <div className={cx(styles.siderbar, 'rich-editor-sidebar')}>
            {mergedTableOfContents && (
              <TableOfContentsPlugin
                {...tableOfContentsProps}
                className={cx(
                  styles.tableOfContnets,
                  'rich-editor-table-of-contents',
                  tableOfContentsProps.className
                )}
                style={{
                  top: toolbar ? EDITOR_TOOLBAR_HEIGHT : 0,
                  ...tableOfContentsProps.style
                }}
              />
            )}
          </div>
        </div>
        {!mergedDisabled && (
          <DraggableBlockPlugin anchorRef={floatingAnchorRef} />
        )}
        {!mergedDisabled && <ShortcutsPlugin />}
        <FormatPainterPlugin />
        <ListPlugin />
        <ImagePlugin />
        <AttachmentPlugin />
        <TablePlugin
          hasCellBackgroundColor
          hasCellMerge
          hasTabHandler
          hasHorizontalScroll={false}
        />
        <TableWrapperPlugin />
        {!mergedDisabled && <TableControllerPlugin />}
        {!mergedDisabled && <TableContextMenuPlugin />}
        {!mergedDisabled && (
          <TableCellResizerPlugin
            isMatchedTarget={cellElement =>
              cellElement.classList.contains('editor-table-cell')
            }
          />
        )}
        <LinkPlugin />
        <QuotePlugin />
        <HorizontalRulePlugin />
        <EmojiPlugin />
        <TabIndentationPlugin />
        <StatePlugin value={value} onChange={onChange} />
        <ModePlugin disabled={mergedDisabled} />
        <HistoryPlugin />
        {debug && <TreeViewPlugin />}
      </div>
      <PreviewAttachment />
    </RichEditorContextProvider>
  )
}
